import { Plus } from "lucide-react";
import { ActiveFilters, Button, ColumnsMenu, FiltersButton, TextInput, Switch, type ColumnToggle } from "@amber/ui";
import { DISCOVERED_LABELS, PASSED_LABELS, type FacetValue, type ItemsResponse } from "../../contracts/index";
import { listOf, toggled } from "../filterList";
import type { BacklogSearch } from "../search";

interface BacklogChip {
  label: string;
  value: string;
  clear: Partial<BacklogSearch>;
}

function facetLabel(facets: readonly FacetValue[] | undefined, key: string): string {
  return facets?.find((f) => f.key === key)?.label ?? key;
}

export function backlogChips(
  search: BacklogSearch,
  data: ItemsResponse | undefined,
  sourceOptions: { sources: readonly { id: string; title: string }[] } | undefined,
): BacklogChip[] {
  return [
    ...(["status", "readiness", "person", "platform", "stage", "family", "type"] as const).flatMap((key) =>
      listOf(search[key]).map((v) => ({
        label: {
          status: "Status",
          readiness: "Readiness",
          person: "Person",
          platform: "Platform",
          stage: "Stage",
          family: "Source family",
          type: "Media",
        }[key],
        value: facetLabel(data?.facets[key], v),
        clear: { [key]: toggled(search[key], v) },
      })),
    ),
    ...(search.discovered
      ? [{ label: "Discovered", value: DISCOVERED_LABELS[search.discovered], clear: { discovered: undefined } }]
      : []),
    ...(search.corrected
      ? [{ label: "Corrected", value: search.corrected === "yes" ? "Yes" : "No", clear: { corrected: undefined } }]
      : []),
    ...(search.counted
      ? [{ label: "Counted", value: "Communications, not copies", clear: { counted: undefined } }]
      : []),
    ...(search.archived ? [{ label: "Reached", value: "Archived", clear: { archived: undefined } }] : []),
    ...(search.passed ? [{ label: "Passed", value: PASSED_LABELS[search.passed], clear: { passed: undefined } }] : []),
    ...(search.through ? [{ label: "Readiness", value: "Not eligible", clear: { through: undefined } }] : []),
    ...(search.overdue ? [{ label: "Past target", value: "Overdue now", clear: { overdue: undefined } }] : []),
    ...listOf(search.language).map((v) => ({
      label: "Language",
      value: v === "none" ? "Not detected yet" : v,
      clear: { language: toggled(search.language, v) },
    })),
    ...(search.source
      ? [
          {
            label: "Source",
            value: sourceOptions?.sources.find((s) => s.id === search.source)?.title ?? "Selected source",
            clear: { source: undefined },
          },
        ]
      : []),
    ...(search.authors === "all"
      ? [{ label: "X posts", value: "Including other accounts", clear: { authors: undefined } }]
      : []),
    ...(search.q ? [{ label: "Title", value: search.q, clear: { q: undefined } }] : []),
  ];
}

function HiddenPosts({ data, set }: { data: ItemsResponse; set: (patch: Partial<BacklogSearch>) => void }) {
  const n = data.other_accounts;
  return (
    <>
      {" · "}
      {String(n)} X {n === 1 ? "post" : "posts"} by other accounts hidden ·{" "}
      <button
        type="button"
        className="text-link"
        onClick={() => {
          set({ authors: "all" });
        }}
      >
        Show
      </button>
    </>
  );
}

function ItemCount({ data, filtered }: { data: ItemsResponse; filtered: boolean }) {
  if (filtered) return `Showing ${String(data.total)} of ${String(data.all)}`;
  return (
    <>
      All <b className="font-semibold text-fg">{data.all}</b> items
    </>
  );
}

export function BacklogToolbar({
  data,
  chips,
  text,
  onText,
  filtersOpen,
  onFilters,
  narrow,
  grouped,
  showsOthers,
  columns,
  onResetColumns,
  set,
  onAdd,
  onReset,
}: {
  data: ItemsResponse | undefined;
  chips: readonly BacklogChip[];
  text: string;
  onText: (text: string) => void;
  filtersOpen: boolean;
  onFilters: () => void;
  narrow: boolean;
  grouped: boolean;
  showsOthers: boolean;
  columns: readonly ColumnToggle[];
  onResetColumns: () => void;
  set: (patch: Partial<BacklogSearch>) => void;
  onAdd: (url: string | null) => void;
  onReset: () => void;
}) {
  const filtered = chips.length > 0;
  return (
    <div className="flex min-h-10 flex-wrap items-center gap-2 px-4 py-2 max-compact:px-2">
      <FiltersButton open={filtersOpen} onToggle={onFilters} />
      <span className="text-ui text-muted" role="status">
        {data ? <ItemCount data={data} filtered={filtered} /> : null}
        {data && data.communications !== null ? (
          <>
            {" · "}
            <b className="font-semibold text-fg" data-testid="communications-count">
              {data.communications}
            </b>{" "}
            {data.communications === 1 ? "communication" : "communications"}
          </>
        ) : null}
        {data && data.other_accounts > 0 && !showsOthers ? <HiddenPosts data={data} set={set} /> : null}
      </span>
      <ActiveFilters
        filters={chips.map((c) => ({
          key: `${c.label}-${c.value}`,
          label: c.label,
          value: c.value,
          onRemove: () => {
            if (c.label === "Title") onText("");
            set(c.clear);
          },
        }))}
        onReset={onReset}
      />
      <span className="flex-1" />
      <label className="sr-only" htmlFor="backlog-filter">
        Filter by title
      </label>
      <TextInput
        id="backlog-filter"
        type="search"
        placeholder="Filter by title"
        style={{ width: narrow ? 140 : 180 }}
        className="h-7"
        value={text}
        onChange={(e) => {
          onText(e.target.value);
        }}
      />
      <Button
        size="sm"
        disabledReason={data && !data.actions.add.enabled ? data.actions.add.reason : null}
        onClick={() => {
          onAdd("");
        }}
      >
        <Plus aria-hidden="true" />
        Add item…
      </Button>
      <Switch
        checked={grouped}
        label="Group by status"
        onChange={(on) => {
          set({ group: on ? undefined : "none" });
        }}
      />
      <ColumnsMenu columns={columns} onReset={onResetColumns} />
    </div>
  );
}
